/**
 * Global features
 *
 * @type Object
 */
import { cloneDeep } from "lodash";
import { http } from "@/services/http";
import {
  ENABLE_HAS_MATCHING_DRIVER_FILTER,
  ENABLE_WHITELISTING_FILTER,
  VIEW_ARCHIVE
} from "@/service/Constants";
import { mapTagFamilies, mergeMapTagFamilies } from "@/utils/TagFamilyMapping";
import { mapDriverFilters } from "@/utils/DriverFilterMapping";
import { mapModuleFilters } from "@/utils/ModuleFilterMapping";
const defaultState = {
  showSpinner: 0,
  activeComponent: null, // either 'module' or 'driver', depending on last clicked row in the playground (cause the playground has 2 lists, one for modules and one drivers)
  filters: [
    {
      type: "checkbox",
      name: "Has matching driver",
      field: "hasMatchingDriver",
      value: true,
      flags: [ENABLE_HAS_MATCHING_DRIVER_FILTER]
    },
    {
      type: "checkbox",
      name: "Released combinations",
      field: "whitelistingEnabled",
      value: true,
      flags: [ENABLE_WHITELISTING_FILTER]
    },
    {
      type: "checkbox",
      name: "Show archive",
      field: "showArchive",
      value: false,
      flags: [VIEW_ARCHIVE]
    }
  ],
  tagFamilies: [],
  modulePinResponse: [],
  driverPinResponse: [],
  checkReload: false
};

const state = cloneDeep(defaultState);

const getters = {
  showSpinner(state) {
    return state.showSpinner > 0;
  },
  filters: state => {
    return state.filters;
  },
  availableFilters: (state, getters, rootState, rootGetters) => {
    return state.filters.filter(filter =>
      rootGetters["brain/auth/canI"](filter.flags)
    );
  },
  allFilters: (state, getters, rootState) => {
    return {
      modulesFilters: rootState.brain.modules.filters,
      driversFilters: rootState.brain.drivers.filters,
      emergencyDriversFilters: rootState.brain.emergencyDrivers.filters,
      globalFilters: getters.filters
    };
  },
  tagFamilies(state) {
    return state.tagFamilies;
  },
  allDisplaySettings: (state, getters, rootGetter) => {
    return {
      savedModuleColumnVisibility:
        rootGetter.brain.ui.playground.savedModuleColumnVisibility,
      savedDriverColumnVisibility:
        rootGetter.brain.ui.playground.savedDriverColumnVisibility,
      savedEmergencyDriverColumnVisibility:
        rootGetter.brain.ui.emergencyDriver
          .savedEmergencyDriverColumnVisibility,
      paneSize: rootGetter.brain.ui.playground.paneSize,
      minimizeModuleInput: rootGetter.brain.ui.playground.minimizeModuleInput,
      minimizeDriverInput: rootGetter.brain.ui.playground.minimizeDriverInput,
      minimizeEmergencyDriver:
        rootGetter.brain.ui.emergencyDriver.minimizeEmergencyDriver,
      showModules: rootGetter.brain.ui.actionBar.showModules,
      showDrivers: rootGetter.brain.ui.actionBar.showDrivers
    };
  },
  modulePinResponse(state) {
    return state.modulePinResponse;
  },
  driverPinResponse(state) {
    return state.driverPinResponse;
  },
  showArchive(state) {
    const filter = state.filters.find(item => item.name === "Show archive");
    if (!filter) {
      return false;
    }
    return filter.value;
  }
};

const mutations = {
  updateFilter: (state, { filter, isChecked }) => {
    state.filters = state.filters.map(item => {
      if (item.name === filter.name) {
        item.value = isChecked;
      }
      return item;
    });
  },

  setArchive: (state, isArchive) => {
    state.filters = state.filters.map(item => {
      if (item.name === "Show archive") {
        item.value = isArchive;
      }
      return item;
    });
  },

  setFilter: (state, data) => {
    state.filters = data;
  },
  setBusy: (state, busy) => {
    if (busy) {
      state.showSpinner++;
    } else {
      state.showSpinner--;
    }
    if (state.showSpinner < 0) state.showSpinner = 0;
  },
  setActiveComponent: (state, name) => {
    state.activeComponent = name;
  },
  setTagFamilies: (state, tagFamilies) => {
    state.tagFamilies = tagFamilies;
  },
  setModulePinResponse: (state, modulePinResponse) => {
    state.modulePinResponse = modulePinResponse;
  },
  setDriverPinResponse: (state, driverPinResponse) => {
    state.driverPinResponse = driverPinResponse;
  },
  setCheckReload: (state, checkReload) => {
    state.checkReload = checkReload;
  },
  resetGlobalFilters: state => {
    state.filters = state.filters.map(item => {
      switch (item.field) {
        case "hasMatchingDriver":
          item.value = true;
          break;
        case "whitelistingEnabled":
          item.value = true;
          break;
        case "showArchive":
          item.value = false;
          break;
      }
      return item;
    });
  }
};

const actions = {
  async forceReload({ dispatch }) {
    dispatch("restart", true);
  },

  async restart({ commit, dispatch, rootGetters }) {
    commit("brain/modules/setModule", null, { root: true });
    commit("brain/modules/setIncompatibleModule", null, { root: true });
    commit("brain/drivers/setDriver", null, { root: true });

    commit("brain/modules/setModules", [], { root: true });
    commit("brain/drivers/setDrivers", [], { root: true });

    dispatch("brain/drivers/resetFilters", null, { root: true });
    dispatch("brain/modules/resetFilters", null, { root: true });

    commit("brain/drivers/setDriverSearchTerm", null, { root: true });
    commit("brain/modules/setModuleSearchTerm", null, { root: true });
    commit("brain/emergencyDrivers/setEmergencyDriverSearchTerm", null, {
      root: true
    });

    commit("brain/global/resetGlobalFilters", null, { root: true });

    dispatch("brain/modules/clearCalculatedPerformance", null, { root: true });
    commit("brain/drivers/clearCalculatedPerformance", null, { root: true });
    commit("brain/emergencyDrivers/clearCalculatedPerformance", null, {
      root: true
    });

    if (rootGetters["brain/solutions/solution"]) {
      commit("brain/solutions/removeEmergencyDriver", this.active, {
        root: true
      });
      commit("brain/solutions/setVirtualModule", null, { root: true });
      commit("brain/solutions/setMultiLengthModules", [], { root: true });
      commit("brain/solutions/setMultiLength", false, { root: true });
      commit("brain/solutions/setModuleFilters", null, { root: true });
      dispatch("brain/solutions/removeModule", this.active, { root: true });
      commit("brain/solutions/removeSensor", this.active, { root: true });
      commit("brain/solutions/removeBridge", this.active, { root: true });
      dispatch("brain/solutions/removeDriver", this.active, { root: true });
    }
  },

  async setTagFamilies({ commit, rootGetters }) {
    // get filters
    const rawModuleFilters = rootGetters["brain/modules/rawFilters"];
    const rawDriverFilters = rootGetters["brain/drivers/rawFilters"];

    // set tagfamily
    const driverTagFamilies = mapTagFamilies(rawDriverFilters);
    const moduleTagFamilies = mapTagFamilies(rawModuleFilters);

    const tagFamilies = mergeMapTagFamilies(
      driverTagFamilies,
      moduleTagFamilies
    );

    commit("setTagFamilies", tagFamilies);
  },

  async getInitialTagFamilies() {
    // get filters
    const rawModuleFilters = await http({
      method: "POST",
      url:
        "/api/v2/modules/data?hasMatchingDriver=true&whitelistingEnabled=true&showArchive=false&size=0",
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }).then(response => {
      return response.data.data.filters;
    });

    const rawDriverFilters = await http({
      method: "POST",
      url:
        "/api/v2/drivers/data?hasMatchingDriver=true&whitelistingEnabled=true&showArchive=false&size=0",
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }).then(response => {
      return response.data.data.filters;
    });

    // set tagfamily
    const driverTagFamilies = mapTagFamilies(rawDriverFilters);
    const moduleTagFamilies = mapTagFamilies(rawModuleFilters);

    const tagFamilies = mergeMapTagFamilies(
      driverTagFamilies,
      moduleTagFamilies
    );

    return tagFamilies;
  },

  async setClearFilters({ rootState, commit }) {
    // get filters
    const rawModuleFilters = await http({
      method: "POST",
      url:
        "/api/v2/modules/data?hasMatchingDriver=true&whitelistingEnabled=true&showArchive=false&size=0",
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }).then(response => {
      return response.data.data.filters;
    });

    const rawDriverFilters = await http({
      method: "POST",
      url:
        "/api/v2/drivers/data?hasMatchingDriver=true&whitelistingEnabled=true&showArchive=false&size=0",
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }).then(response => {
      return response.data.data.filters;
    });

    commit(
      "brain/modules/setFilters",
      mapModuleFilters(rootState.brain.region, rawModuleFilters),
      { root: true }
    );
    commit(
      "brain/drivers/setFilters",
      mapDriverFilters(rootState.brain.region, rawDriverFilters),
      { root: true }
    );

    commit("brain/modules/setModuleSearchTerm", null, { root: true });

    commit("brain/drivers/setDriverSearchTerm", null, { root: true });

    if (rootState.brain.region === "na" || rootState.brain.region === "emea") {
      const product =
        rootState.brain.region === "na" ? "bodine" : "trust-sight";
      const rawEmergencyDriverFilters = await http({
        method: "POST",
        url:
          "/api/v2/drivers/" +
          product +
          "/data?hasMatchingDriver=true&whitelistingEnabled=true&showArchive=false&size=0",
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }).then(response => {
        return response.data.data.filters;
      });

      commit(
        "brain/emergencyDrivers/setFilters",
        mapDriverFilters(rootState.brain.region, rawEmergencyDriverFilters),
        { root: true }
      );

      commit("brain/emergencyDrivers/setEmergencyDriverSearchTerm", null, {
        root: true
      });
    }
  },

  async saveAllFilters({ getters, rootState }, description) {
    return await http({
      method: "put",
      url: `/api/v2/users/profile/settings/filters/save`,
      data: {
        name: description,
        filters: getters.allFilters,
        region: rootState.brain.region
      }
    });
  },
  async autoSaveAllFilters({ getters, rootState }) {
    return await http({
      method: "put",
      url: `/api/v2/users/profile/settings/filters/save`,
      data: {
        name: "AUTOSAVE",
        filters: getters.allFilters,
        region: rootState.brain.region
      }
    });
  },
  async fetchLatestFilter() {
    return http({
      method: "get",
      url: "/api/v2/users/profile/settings/filters/latest"
    }).catch(err => {
      if (err.response.status === 401) {
        this._vm.$snotify["error"]("Token expired, please log in again.");
      } else {
        console.log(err);
      }
    });
  },

  async resetAllFilters({ commit, rootGetters }) {
    commit(
      "brain/modules/setFilters",
      [...rootGetters["brain/modules/emptyInitialFilters"]],
      { root: true }
    );
    commit(
      "brain/drivers/setFilters",
      [...rootGetters["brain/drivers/emptyInitialFilters"]],
      { root: true }
    );
  },

  async resetAllFiltersWithSearchTerm(
    { commit, dispatch, rootGetters },
    { searchTerm, component }
  ) {
    await commit(
      "brain/modules/setFilters",
      [...rootGetters["brain/modules/emptyInitialFilters"]],
      { root: true }
    );
    await commit(
      "brain/drivers/setFilters",
      [...rootGetters["brain/drivers/emptyInitialFilters"]],
      { root: true }
    );

    //e = searchTerm
    if (component === "module") {
      //commit searchTerm to module store
      await commit("brain/modules/setModuleSearchTerm", searchTerm, {
        root: true
      });
    } else {
      //commit searchTerm to driver store
      await commit("brain/drivers/setDriverSearchTerm", searchTerm, {
        root: true
      });
    }

    return {
      modules: await dispatch("brain/modules/getModules", null, { root: true }),
      drivers: await dispatch("brain/drivers/getDrivers", null, { root: true })
    };
  },

  async saveAllDisplaySettings({ getters, rootState }, description) {
    return await http({
      method: "put",
      url: `/api/v2/users/profile/settings/views/save`,
      data: {
        name: description,
        view: getters.allDisplaySettings,
        region: rootState.brain.region
      }
    });
  },

  async autoSaveAllDisplaySettings({ getters, rootState }) {
    return await http({
      method: "put",
      url: `/api/v2/users/profile/settings/views/save`,
      data: {
        name: "AUTOSAVE",
        view: getters.allDisplaySettings,
        region: rootState.brain.region
      }
    });
  },

  async fetchLatestDisplaySettings() {
    return http({
      method: "get",
      url: "/api/v2/users/profile/settings/views/latest"
    }).catch(err => {
      if (err.response.status === 401) {
        this._vm.$snotify["error"]("Token expired, please log in again.");
      } else {
        console.log(err);
      }
    });
  },

  async savePinnedProduct({ rootState }, { uid, type }) {
    return await http({
      method: "post",
      url: `/api/v2/users/profile/preferred-products/`,
      data: {
        uid: uid,
        region: rootState.brain.region,
        productType: type
      }
    });
  },

  async deletePinnedProduct({ dispatch, getters }, { type, uid }) {
    let pinToDelete = null;
    if (type === "module") {
      pinToDelete = getters.modulePinResponse.find(pin => pin.uid === uid);
    } else {
      pinToDelete = getters.driverPinResponse.find(pin => pin.uid === uid);
    }

    return await http({
      method: "delete",
      url: `/api/v2/users/profile/preferred-products/${pinToDelete.id}`
    }).then(() => {
      dispatch("getPinnedProducts");
    });
  },

  async getPinnedProducts({ commit, rootState }) {
    http({
      method: "get",
      url: "/api/v2/users/profile/preferred-products/all"
    })
      .then(response => {
        if (response.data.data) {
          const moduleResponse = response.data.data
            .filter(pin => pin.productType === "module")
            .filter(pin => pin.region === rootState.brain.region);

          const driverResponse = response.data.data
            .filter(pin => pin.productType === "driver")
            .filter(pin => pin.region === rootState.brain.region);

          commit("setModulePinResponse", moduleResponse);
          commit("setDriverPinResponse", driverResponse);

          const pinnedModulesUid = moduleResponse.map(module => module.uid);
          if (pinnedModulesUid.length) {
            http({
              method: "post",
              url: "api/v2/modules/data?uid[]=" + pinnedModulesUid
            })
              .then(response => {
                commit(
                  "brain/modules/setPinnedModules",
                  response.data.data.hits,
                  { root: true }
                );
              })
              .catch(err => {
                console.log(err);
              });
          } else {
            commit("brain/modules/setPinnedModules", [], { root: true });
          }

          const pinnedDriversUid = driverResponse.map(driver => driver.uid);
          if (pinnedDriversUid.length) {
            http({
              method: "post",
              url: "api/v2/drivers/data?uid[]=" + pinnedDriversUid
            })
              .then(response => {
                commit(
                  "brain/drivers/setPinnedDrivers",
                  response.data.data.hits,
                  { root: true }
                );
              })
              .catch(err => {
                console.log(err);
              });
          } else {
            commit("brain/drivers/setPinnedDrivers", [], { root: true });
          }
        }
      })
      .catch(err => {
        console.log(err);
      });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
